import { useQuery } from '@apollo/client';
import { Balance } from 'components/Balance/Balance';
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { Button } from 'components/Button/Button';
import { Container } from 'components/Container/Container';
import { ExportMembers } from 'components/ExportMembers/ExportMembers';
import { GenerateBible } from 'components/GenerateBible/GenerateBible';
import { Icon } from 'components/Icon/Icon';
import { Loader } from 'components/Loader/Loader';
import { MembersTabs } from 'components/MembersTabs/MembersTabs';
import { PageTitle } from 'components/PageTitle/PageTitle';
import { Pagination } from 'components/Pagination/Pagination';
import { PropertiesFilter } from 'components/PropertiesFilter/PropertiesFilter';
import { SearchFilter } from 'components/SearchFilter/SearchFilter';
import { ShareholderBadge } from 'components/ShareholderBadge/ShareholderBadge';
import { SortFilter } from 'components/SortFilter/SortFilter';
import { Status } from 'components/Status/Status';
import { StatusFilter } from 'components/StatusFilter/StatusFilter';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/Table/Table';
import dayjs from 'dayjs';
import { normalizeArray } from 'lib/utils';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AppRoutes } from 'Routes';
import { USERS } from 'services/users/query';

const PAGE_SIZE = parseInt(localStorage.getItem('pageSize') as any) || 10;
const STATUS = ['ACTIVE', 'INACTIVE', 'SUSPENDED', 'SUGGESTED', 'BLOCKED', 'DRAFT', 'TERMINATED', 'SCHEDULED'];
const SORT = [
    { value: 'A_Z', label: 'A-Z' },
    { value: 'Z_A', label: 'Z-A' },
    { value: 'MOST_RECENT', label: 'Most recent' },
    { value: 'LEAST_RECENT', label: 'Least recent' },
];
const PROPERTIES = ['shareholder', 'remainder_balance', 'negative_balance'];
export const MembersList = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { data, loading, refetch } = useQuery(USERS, {
        variables: { isMember: true, first: PAGE_SIZE },
    });
    const navigate = useNavigate();
    const { t } = useTranslation('translation', { keyPrefix: 'members' });
    const { t: b } = useTranslation('translation');
    const { t: s } = useTranslation('translation', { keyPrefix: 'status' });

    const activeStatuses = searchParams.get('status')?.split(',') || ['ACTIVE'];
    const statusLabels = activeStatuses.map((code) => s(code, code)).join(', ');
    const clearStatusFilter = () => {
        const next = new URLSearchParams(searchParams);
        next.delete('status');
        setSearchParams(next, { replace: true });
    };

    useEffect(() => {
        const search = searchParams.get('search');
        const sort = searchParams.get('sort') || 'A_Z';
        const status = searchParams.get('status')?.split(',') || ['ACTIVE'];
        const page = searchParams.get('page') || '1';
        const property = searchParams.get('property');

        const endedStatuses = ['TERMINATED', 'INACTIVE', 'BLOCKED'];
        const showingEnded = status.some((s) => endedStatuses.includes(s));

        refetch({
            search: search,
            sort: sort,
            status: status,
            offset: (parseInt(page) - 1) * PAGE_SIZE,
            isMember: showingEnded ? null : true,
            isShareholder: property === 'shareholder' ? true : null,
            hasNegativeBalance: property === 'negative_balance' ? true : null,
            hasRemainderBalance: property === 'remainder_balance' ? true : null,
        });
    }, [searchParams, refetch]);

    const previewMember = (id: string) => {
        navigate(`${AppRoutes.memberPreview.replace(':id', id)}`);
    };

    if (loading) return <Loader fullSize />;

    return (
        <Container>
            <Breadcrumbs crumbs={[b('breadcrumbs.dashboard'), b('breadcrumbs.members')]} />
            <MembersTabs />
            <PageTitle>{t('title')}</PageTitle>

            <div className="flex items-center gap-2 pb-3">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-100 text-zinc-700 text-sm">
                    {t('filterChipLabel', { statuses: statusLabels })}
                    <button
                        type="button"
                        className="leading-none text-zinc-500 hover:text-zinc-900"
                        aria-label={t('filterChipClear')}
                        onClick={clearStatusFilter}
                    >
                        ×
                    </button>
                </span>
            </div>

            <div className="flex justify-between pb-4 gap-4 flex-col md:flex-row">
                <div className="flex gap-4 flex-wrap">
                    <SearchFilter placeholder={t('searchPlaceholder')} />
                    <SortFilter options={SORT} />
                    <StatusFilter options={STATUS} />
                    <PropertiesFilter options={PROPERTIES} />
                </div>
                <div className="flex gap-2">
                    <ExportMembers />
                    <GenerateBible />
                    <Button asChild>
                        <Link to={AppRoutes.newMember}>{t('addMember')}</Link>
                    </Button>
                </div>
            </div>

            <Table className="border">
                <TableHeader>
                    <TableRow>
                        <TableHead>{t('nameLabel')}</TableHead>
                        <TableHead className="hidden sm:table-cell">{t('emailLabel')}</TableHead>
                        <TableHead className="hidden md:table-cell">{t('phoneLabel')}</TableHead>
                        {searchParams.get('property') === 'remainder_balance' && (
                            <TableHead className="hidden lg:table-cell">{t('remainderBalanceLabel')}</TableHead>
                        )}
                        {searchParams.get('property') === 'negative_balance' && (
                            <TableHead className="hidden lg:table-cell">{t('balanceLabel')}</TableHead>
                        )}
                        <TableHead className="hidden lg:table-cell">{t('activeLabel')}</TableHead>
                        <TableHead>{t('statusLabel')}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody className="hover:cursor-pointer">
                    {data?.users.edges.length > 0 &&
                        normalizeArray(data?.users).map((user: any) => (
                            <TableRow
                                key={user.id}
                                onClick={() => previewMember(user.id)}
                                className="text-zinc-700 hover:bg-rose-50"
                            >
                                <TableCell className="flex gap-2 items-center">
                                    <UserImage image={user.image} />
                                    {user.lastName || '-'}, {user.firstName || '-'}
                                    {user.isShareholder && <ShareholderBadge small />}
                                </TableCell>
                                <TableCell className="hidden sm:table-cell">{user.email || '-'}</TableCell>
                                <TableCell className="hidden md:table-cell">{user.phoneNumber || '-'}</TableCell>
                                {searchParams.get('property') === 'remainder_balance' && (
                                    <TableCell className="hidden lg:table-cell">
                                        <Balance balance={{ amount: user.remainderBalance, currency: 'CHF' }} />
                                    </TableCell>
                                )}
                                {searchParams.get('property') === 'negative_balance' && (
                                    <TableCell className="hidden lg:table-cell">
                                        <Balance balance={user.balance} />
                                    </TableCell>
                                )}
                                <TableCell className="hidden lg:table-cell">
                                    {dayjs(user.dateJoined).format('DD MMM YYYY')}
                                </TableCell>
                                <TableCell>
                                    <Status status={user.status} />
                                </TableCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
            {data?.users?.edges?.length === 0 && (
                <div className={'py-5 text-center text-zinc-700'}>{t('noResults')}</div>
            )}
            <Pagination pageInfo={data?.users?.pageInfo} total={data?.users?.totalCount} />
        </Container>
    );
};

const UserImage = ({ image }: any) => {
    return (
        <div className="bg-zinc-300 w-[32px] h-[32px] flex justify-center items-center">
            {image ? (
                <img src={`${image.url}?fit=facearea&facepad=2.5&w=32&h=32`} alt={image.alt} />
            ) : (
                <Icon name="user" className="text-zinc-50 h-[18px]" />
            )}
        </div>
    );
};
